import { useQueries, useQuery } from '@tanstack/react-query'
import { fetchApiWithToken, getBatchFetcher } from '~/api/utils'
import type { ContactHeadResponse } from '../types'

/** A contact has no picture of its own: the API returns its company logo. */
export type ContactPicture = {
  profilePicUrl: string | null
  contact: ContactHeadResponse
}

type ContactPictureWithId = ContactPicture & { id: number }

const contactPicturesBatcher = () =>
  getBatchFetcher<ContactPictureWithId>('contactPictures', async (ids) => {
    const pictures = await fetchApiWithToken<ContactPicture[]>({
      method: 'get',
      url: `/v1/contacts/profile_pics?id[]=${ids.join('&id[]=')}&itemsPerPage=1000`
    })

    return pictures.map((picture) => ({ ...picture, id: picture.contact.id }))
  })

export const useContactsPictures = ({ contactIds }: { contactIds: number[] }) =>
  useQueries({
    queries: contactIds.map((contactId) => ({
      queryKey: ['contacts', 'picture', contactId],
      queryFn: () => contactPicturesBatcher().fetch(contactId),
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false
    }))
  })

export const useContactPicture = ({ contactId }: { contactId: number }) =>
  useQuery({
    queryKey: ['contacts', 'picture', contactId],
    queryFn: () => contactPicturesBatcher().fetch(contactId),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false
  })
